/**
 * Centralized route parameter validation to prevent XSS attacks
 * Implements allowlist-based validation for different parameter types
 */

// Parameter type definitions with their allowed character sets
export const PARAM_TYPES = {
  // Alphanumeric with hyphens and underscores (for IDs, names)
  IDENTIFIER: /^[a-zA-Z0-9_-]+$/,
  // Numbers only
  NUMERIC: /^[0-9]+$/,
  // Alphanumeric with spaces, hyphens, underscores (for display names)
  DISPLAY_NAME: /^[a-zA-Z0-9 _-]+$/,
  // UUIDs
  UUID: /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i,
  // Entity names (lowercase alphanumeric)
  ENTITY_NAME: /^[a-z0-9_]+$/,
  FILE_EXTENSION: /^[a-zA-Z0-9]+$/,
  // Version patterns (e.g., v1, v2.0, v1.2.3)
  VERSION: /^v?[0-9]+(\.[0-9]+)*$/,
} as const

export type ParamType = keyof typeof PARAM_TYPES

export interface ValidationResult {
  isValid: boolean
  sanitized: string
  error?: string
}

/**
 * Validates a route parameter against a specific type pattern
 * Returns validation result with sanitized value
 */
export function validateRouteParam(value: string | undefined, type: ParamType): ValidationResult {
  if (!value) {
    return {
      isValid: false,
      sanitized: '',
      error: 'Parameter is required',
    }
  }

  const pattern = PARAM_TYPES[type]
  const isValid = pattern.test(value)

  if (!isValid) {
    return {
      isValid: false,
      sanitized: '',
      error: `Invalid ${type.toLowerCase()} format`,
    }
  }

  return {
    isValid: true,
    sanitized: value,
  }
}

/**
 * Sanitizes a parameter by removing dangerous characters
 * This is a fallback for cases where strict validation might break functionality
 * Always prefer validateRouteParam for new code
 */
export function sanitizeParam(value: string | undefined): string {
  if (!value) return ''
  // Remove common XSS vectors while preserving most valid characters
  return value.replace(/[<>'"&]/g, '')
}

/**
 * Type-safe parameter validation map
 * Maps route parameter names to their expected types for different entities (unis, nodes, etc)
 */
export const ROUTE_PARAM_CONFIG: Record<string, ParamType> = {
  id: 'IDENTIFIER',
  uniName: 'IDENTIFIER',
  uniId: 'IDENTIFIER',

  node: 'IDENTIFIER',
  nodeName: 'IDENTIFIER',
  nodeId: 'IDENTIFIER',

  entity: 'ENTITY_NAME',
  entityName: 'ENTITY_NAME',
  entityId: 'IDENTIFIER',

  transactionId: 'IDENTIFIER',
  blockId: 'IDENTIFIER',

  fileId: 'IDENTIFIER',

  smartContractId: 'IDENTIFIER',
  smartContractVersionOrdinal: 'NUMERIC',

  dataProductId: 'IDENTIFIER',
  productId: 'IDENTIFIER',
  tableId: 'IDENTIFIER',
  tableName: 'IDENTIFIER',
  table: 'IDENTIFIER',

  state: 'IDENTIFIER',
  code: 'IDENTIFIER',

  roleId: 'IDENTIFIER',

  connectionId: 'IDENTIFIER',

  recipeId: 'IDENTIFIER',
}

/**
 * Validates all parameters in a params object
 * Returns validated params or throws if validation fails
 */
export function validateAllParams(params: Record<string, string | undefined>): Record<string, string> {
  const validated: Record<string, string> = {}
  const errors: string[] = []

  for (const [key, value] of Object.entries(params)) {
    const paramType = ROUTE_PARAM_CONFIG[key]

    if (!paramType) {
      // Unknown parameter - apply strict sanitization
      validated[key] = sanitizeParam(value)
      continue
    }

    const result = validateRouteParam(value, paramType)

    if (!result.isValid) {
      errors.push(`${key}: ${result.error}`)
    } else {
      validated[key] = result.sanitized
    }
  }

  if (errors.length > 0) {
    throw new Error(`Invalid route parameters: ${errors.join(', ')}`)
  }

  return validated
}
